import { useState, useCallback } from 'react';

/**
 * Reusable toggle-set hook for expand/collapse state.
 * Returns a Set<string> with toggle, addAll, clear, and has helpers.
 */
export function useToggleSet(initial?: Iterable<string>) {
  const [set, setSet] = useState<Set<string>>(() => new Set(initial));

  const toggle = useCallback((id: string) => {
    setSet(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const addAll = useCallback((ids: string[]) => {
    setSet(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.add(id));
      return next;
    });
  }, []);

  const replace = useCallback((ids: string[]) => {
    setSet(new Set(ids));
  }, []);

  const clear = useCallback(() => {
    setSet(new Set());
  }, []);

  const has = useCallback((id: string) => set.has(id), [set]);

  return { set, toggle, addAll, replace, clear, has } as const;
}
